import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Apple, Android, Settings, Shield, Download, Cloud, Zap, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Link } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const ApplePage = ({ onBack, guides }) => {
  const navigate = useNavigate();
  // Function to count procedures in a guide by analyzing internal links
  const countProceduresInGuide = (guideId) => {
    const guide = guides?.find(g => g.id === guideId);
    if (!guide || !guide.content || !guide.content.sections) return 0;
    
    let linkCount = 0;
    guide.content.sections.forEach(section => {
      if (section.content) {
        // Count links in format [text](link-id)
        const links = section.content.match(/\[([^\]]+)\]\(([^)]+)\)/g);
        if (links) {
          linkCount += links.length;
        }
      }
    });
    return linkCount;
  };

  const appleCategories = [
    {
      id: 'iphone-setup',
      name: 'Configuration iPhone',
      icon: Apple,
      description: 'Configuration initiale, transfert de données et personnalisation',
      gradient: 'from-gray-500 to-slate-600',
      guideId: 'iphone-configuration',
      hasGuide: true
    },
    {
      id: 'ios-apps',
      name: 'Applications iOS',
      icon: Download,
      description: 'Gestion des apps, App Store et mises à jour',
      gradient: 'from-blue-500 to-indigo-600',
      guideId: 'ios-apps-management',
      hasGuide: true
    },
    {
      id: 'icloud-sync',
      name: 'Synchronisation iCloud',
      icon: Cloud,
      description: 'Photos, contacts, calendriers et documents',
      gradient: 'from-cyan-500 to-blue-600',
      guideId: 'icloud-sync',
      hasGuide: true
    },
    {
      id: 'apple-security',
      name: 'Sécurité Apple',
      icon: Shield,
      description: 'Touch ID, Face ID, mots de passe et confidentialité',
      gradient: 'from-green-500 to-emerald-600',
      guideId: 'apple-security',
      hasGuide: true
    },
    {
      id: 'productivity',
      name: 'Productivité Apple',
      icon: Zap,
      description: 'Raccourcis, Siri et automatisation',
      gradient: 'from-orange-500 to-red-600',
      guideId: 'apple-productivity',
      hasGuide: true
    },
    {
      id: 'apple-ecosystem',
      name: 'Écosystème Apple',
      icon: Settings,
      description: 'Mac, iPad, Apple Watch et continuité',
      gradient: 'from-purple-500 to-pink-600',
      guideId: 'apple-ecosystem',
      hasGuide: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb />
        </div>
      </div>

      {/* Hero Banner */}
      <section className="relative overflow-hidden bg-gradient-to-r from-gray-600 via-slate-600 to-zinc-600">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-3xl mb-8">
              <Apple className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
              Écosystème Apple
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 max-w-3xl mx-auto">
              Maîtrisez votre iPhone, iPad, Mac et tous les services Apple pour une productivité optimale
            </p>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-gray-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-slate-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-zinc-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Apple Guides Section */}
        <section className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-3xl p-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-gray-400 to-slate-500 rounded-2xl mb-6">
              <Apple className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Guides Apple Pratiques
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Exploitez au maximum vos appareils Apple avec nos guides détaillés
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {appleCategories.map((category, index) => {
              const IconComponent = category.icon;
              const procedureCount = category.hasGuide ? countProceduresInGuide(category.guideId) : 0;
              
              return (
                <Card 
                  key={index} 
                  className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-0 overflow-hidden relative h-full flex flex-col"
                  onClick={() => {
                    if (category.hasGuide) {
                      console.log('Apple Guide selected:', { id: category.guideId });
                      navigate(`/mobile/guides/${category.guideId}`);
                    }
                  }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <CardHeader className="text-center pb-4 relative z-10 flex-grow">
                    <div className={`mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br ${category.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                      {category.name}
                    </CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed flex-grow">
                      {category.description}
                    </CardDescription>
                    
                    {category.hasGuide && procedureCount > 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {procedureCount} procédure{procedureCount > 1 ? 's' : ''}
                        </Badge>
                      </div>
                    )}
                    
                    {category.hasGuide && procedureCount === 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          1 guide
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  
                  {/* Bottom Action Bar */}
                  <div className={`bg-gray-900 ${category.hasGuide ? `group-hover:bg-gradient-to-r group-hover:${category.gradient}` : ''} transition-all duration-300 p-4 relative z-10`}>
                    <Button 
                      variant="ghost"
                      disabled={!category.hasGuide}
                      className={`w-full font-bold text-lg hover:bg-transparent ${
                        category.hasGuide 
                          ? 'text-white' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {category.hasGuide ? 'Consulter le guide' : 'Bientôt disponible'}
                      {category.hasGuide && <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />}
                    </Button>
                  </div>
                  
                  {category.hasGuide && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <Link to="/mobile">
            <Button className="bg-gradient-to-r from-gray-600 to-slate-600 hover:from-gray-700 hover:to-slate-700 text-white font-bold px-8 py-4 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300">
              <ArrowLeft className="h-6 w-6 mr-3" />
              Retour aux appareils mobiles
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ApplePage;